import { v4 as uuidv4 } from "uuid"

import { UITypes } from "types"

import { stringifyDateRange } from "utils/dateUtils"
import { transformText } from "utils/transformText"
import useUrlQueryParams from "hooks/useUrlQueryParams"

import styles from "./index.module.scss"

interface FilterTagProps {
  additionalTags?: UITypes.AdditionalTag[]
  clearAdditionalTags?: () => void
  skippedTags?: string[]
  headerCells: UITypes.TableHeaderCell[]
}

export const FilterTags = ({ additionalTags, clearAdditionalTags, skippedTags = [], headerCells }: FilterTagProps) => {
  const { originalQueryParams, setUrlQueryParams } = useUrlQueryParams()
  const orderHeaderCell = headerCells.find(item => item.name === originalQueryParams["orderColumn"])
  const hasSortTag = orderHeaderCell && originalQueryParams["orderType"] && !skippedTags.includes("orderColumn")
  const tags = headerCells
    .filter(item => !["checkbox"].includes(item.type || "") && !skippedTags.includes(item.name || ""))
    .filter(
      item =>
        (Object.hasOwn(originalQueryParams, item.name || "") && originalQueryParams[item.name || ""] !== "") ||
        ((item.type === "sort-date-range" || item.type === "date-range") &&
          Object.hasOwn(originalQueryParams, `${item.name}_start`) &&
          originalQueryParams[`${item.name}_start`] !== "") ||
        (Object.hasOwn(originalQueryParams, `${item.name}_end`) && originalQueryParams[`${item.name}_end`] !== ""),
    )

  const getFilterValue = (item: UITypes.TableHeaderCell) => {
    const value = item.name || ""

    if (["select", "color-select"].includes(item.type || "")) {
      return item.options?.find(option => option.value === originalQueryParams[value])?.label
    }

    if (item.type === "sort-date-range" || item.type === "date-range") {
      const startDate = originalQueryParams[`${value}_start`]
      const endDate = originalQueryParams[`${value}_end`]

      return stringifyDateRange([startDate, endDate], true)
    }

    return transformText(originalQueryParams[value] || "")
  }

  const handleFilterItemClear = (key: string, itemType?: string) => {
    if (key === "") {
      clearAdditionalTags?.()
      return
    }

    if (itemType === "sort-date-range" || itemType === "date-range") {
      setUrlQueryParams({ [`${key}_start`]: null, [`${key}_end`]: null })
      return
    }

    setUrlQueryParams({ [key]: null, ...(key === "orderColumn" ? { orderType: null } : {}) })
  }

  const renderFilterTags = (
    key: string | undefined,
    label: string,
    itemClearFunction?: (key: string, itemType: string | undefined) => void,
    value?: string,
    itemType?: string,
  ) => {
    return (
      <li className={styles.filterTag} key={key}>
        <span className={styles.filterTagName}>{label}</span>
        <span className={styles.filterTagValues}>{value}</span>
        {itemClearFunction && (
          <span onClick={() => itemClearFunction(key || "", itemType)} className={styles.filterTagClose}></span>
        )}
      </li>
    )
  }

  const renderAdditionalTags = () =>
    additionalTags?.map(item => {
      const values = item.values
      const shownValues = [...values.slice(0, 3), ...(values.length - 3 > 0 ? [`+${values.length - 3}`] : [])]

      return renderFilterTags(uuidv4(), item.label, clearAdditionalTags, shownValues.join(", "))
    })

  const renderSortTag = () => {
    if (
      skippedTags.includes("orderColumn") ||
      !originalQueryParams["orderType"] ||
      !originalQueryParams["orderColumn"] ||
      !orderHeaderCell
    ) {
      return null
    }

    return renderFilterTags(
      "orderColumn",
      orderHeaderCell.label || orderHeaderCell.title,
      handleFilterItemClear,
      originalQueryParams["orderType"] || "",
    )
  }

  if (tags.length || additionalTags?.length || hasSortTag) {
    return (
      <div className={styles.filterTagsWrapper}>
        <h3 className={styles.filterTitle}>Selected filter(s)</h3>
        <ul className={styles.filterTagsList}>
          {renderAdditionalTags()}
          {renderSortTag()}
          {tags.map(item =>
            renderFilterTags(
              item.name,
              item.label || item.title,
              handleFilterItemClear,
              getFilterValue(item),
              item.type,
            ),
          )}
        </ul>
      </div>
    )
  }

  return null
}
